import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Card, Button, TextInput, Badge, Spinner, Modal, Label, Select } from 'flowbite-react';
import {
  HiPlus,
  HiPencil,
  HiTrash,
  HiRefresh,
  HiSearch,
  HiCheckCircle,
  HiXCircle,
  HiExclamationCircle,
  HiInformationCircle,
  HiCurrencyDollar,
  HiUsers,
  HiHome,
} from 'react-icons/hi';
import AdminLayout from '@/components/layout/AdminLayout';
import Pagination from '@/components/Pagination';

interface Price {
  id: string;
  key: string;
  label: string;
  value: string;
  min_quantity: number;
  group: 'comunidad' | 'familiar' | 'empresa';
  order: number;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export default function AdminPrices() {
  const { userProfile } = useAuth();
  const [prices, setPrices] = useState<Price[]>([]);
  const [filteredPrices, setFilteredPrices] = useState<Price[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [groupFilter, setGroupFilter] = useState<string>('all');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [editingPrice, setEditingPrice] = useState<Price | null>(null);
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [messageModal, setMessageModal] = useState({ type: 'success' as 'success' | 'error' | 'info', title: '', message: '', show: false });

  // Estados de paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [displayedPrices, setDisplayedPrices] = useState<Price[]>([]);
  const [formData, setFormData] = useState({
    key: '',
    label: '',
    value: '0',
    min_quantity: 0,
    group: 'comunidad' as 'comunidad' | 'familiar' | 'empresa',
    order: 0,
    description: '',
  });

  const loadPrices = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('prices')
        .select('*')
        .order('group', { ascending: true })
        .order('order', { ascending: true });

      if (error) throw error;
      setPrices(data || []);
      setFilteredPrices(data || []);
    } catch (error: any) {
      console.error('Error loading prices:', error);
      showMessage('error', 'Error', 'Error al cargar precios: ' + (error.message || 'Error desconocido'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userProfile?.role === 'superadmin') {
      loadPrices();
    }
  }, [userProfile]);

  useEffect(() => {
    let filtered = prices;
    if (searchTerm) {
      filtered = filtered.filter(
        (price) =>
          price.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
          price.key.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (groupFilter !== 'all') {
      filtered = filtered.filter((price) => price.group === groupFilter);
    }
    setFilteredPrices(filtered);
    setCurrentPage(1); // Resetear a la primera página cuando cambia el filtro
  }, [searchTerm, groupFilter, prices]);

  // Calcular precios a mostrar según paginación
  useEffect(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setDisplayedPrices(filteredPrices.slice(startIndex, endIndex));
  }, [filteredPrices, currentPage, itemsPerPage]);

  const showMessage = (type: 'success' | 'error' | 'info', title: string, message: string) => {
    setMessageModal({ type, title, message, show: true });
  };

  const showConfirm = (message: string, action: () => void) => {
    setConfirmMessage(message);
    setConfirmAction(() => action);
    setShowConfirmModal(true);
  };

  const openEditModal = (price?: Price) => {
    if (price) {
      setEditingPrice(price);
      setFormData({
        key: price.key || '',
        label: price.label || '',
        value: price.value || '0',
        min_quantity: price.min_quantity || 0,
        group: price.group || 'comunidad',
        order: price.order || 0,
        description: price.description || '',
      });
    } else {
      setEditingPrice(null);
      setFormData({
        key: '',
        label: '',
        value: '0',
        min_quantity: 0,
        group: 'comunidad',
        order: 0,
        description: '',
      });
    }
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingPrice(null);
  };

  const savePrice = async () => {
    try {
      if (editingPrice) {
        const { error } = await supabase
          .from('prices')
          .update({
            label: formData.label,
            value: formData.value,
            min_quantity: formData.min_quantity,
            group: formData.group,
            order: formData.order,
            description: formData.description || null,
          })
          .eq('id', editingPrice.id);

        if (error) throw error;
        showMessage('success', 'Precio Actualizado', 'El precio ha sido actualizado exitosamente');
      } else {
        const { error } = await supabase
          .from('prices')
          .insert({
            key: formData.key,
            label: formData.label,
            value: formData.value,
            min_quantity: formData.min_quantity,
            group: formData.group,
            order: formData.order,
            description: formData.description || null,
          });

        if (error) throw error;
        showMessage('success', 'Precio Creado', 'El precio ha sido creado exitosamente');
      }
      closeEditModal();
      await loadPrices();
    } catch (error: any) {
      console.error('Error saving price:', error);
      showMessage('error', 'Error', 'Error al guardar precio: ' + (error.message || 'Error desconocido'));
    }
  };

  const deletePrice = async (price: Price) => {
    showConfirm(
      `¿Estás seguro de que deseas eliminar el precio "${price.label}"? Esta acción no se puede deshacer.`,
      async () => {
        try {
          const { error } = await supabase
            .from('prices')
            .delete()
            .eq('id', price.id);

          if (error) throw error;
          showMessage('success', 'Precio Eliminado', 'El precio ha sido eliminado exitosamente');
          await loadPrices();
        } catch (error: any) {
          console.error('Error deleting price:', error);
          showMessage('error', 'Error', 'Error al eliminar precio: ' + (error.message || 'Error desconocido'));
        } finally {
          setShowConfirmModal(false);
        }
      }
    );
  };

  const getGroupBadge = (group: string) => {
    const config = {
      comunidad: { color: 'blue' as const, label: 'Comunidad' },
      familiar: { color: 'purple' as const, label: 'Familiar' },
      empresa: { color: 'gray' as const, label: 'Empresa' },
    };
    const groupConfig = config[group as keyof typeof config] || config.comunidad;
    return <Badge color={groupConfig.color}>{groupConfig.label}</Badge>;
  };

  if (!userProfile || userProfile.role !== 'superadmin') {
    return (
      <AdminLayout>
        <Card>
          <div className="p-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Acceso Denegado</h3>
            <p className="text-gray-600 dark:text-gray-400">No tienes permisos para acceder a esta página</p>
          </div>
        </Card>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Gestión de Precios</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">Administra los precios del sistema</p>
          </div>
          <Button color="blue" onClick={() => openEditModal()} className="gap-2">
            <HiPlus className="w-4 h-4" />
            Nuevo Precio
          </Button>
        </div>

        {/* Estadísticas - Compactas */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="bg-white dark:bg-gray-800 py-2 px-3">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-600 dark:text-gray-400 truncate">Total</p>
                <p className="text-lg font-bold text-gray-900 dark:text-white">{prices.length}</p>
              </div>
              <HiCurrencyDollar className="w-5 h-5 text-blue-600 flex-shrink-0 ml-2" />
            </div>
          </Card>
          <Card className="bg-white dark:bg-gray-800 py-2 px-3">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-600 dark:text-gray-400 truncate">Comunidad</p>
                <p className="text-lg font-bold text-green-600">
                  {prices.filter((p) => p.group === 'comunidad').length}
                </p>
              </div>
              <HiHome className="w-5 h-5 text-green-600 flex-shrink-0 ml-2" />
            </div>
          </Card>
          <Card className="bg-white dark:bg-gray-800 py-2 px-3">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-600 dark:text-gray-400 truncate">Familiar</p>
                <p className="text-lg font-bold text-purple-600">
                  {prices.filter((p) => p.group === 'familiar').length}
                </p>
              </div>
              <HiUsers className="w-5 h-5 text-purple-600 flex-shrink-0 ml-2" />
            </div>
          </Card>
        </div>

        {/* Búsqueda y filtros */}
        <Card className="bg-white dark:bg-gray-800">
          <div className="p-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <TextInput
                  type="text"
                  placeholder="Buscar por nombre o clave..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
                <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              </div>
              <div className="flex gap-2">
                <Select
                  value={groupFilter}
                  onChange={(e) => setGroupFilter(e.target.value)}
                >
                  <option value="all">Todos los grupos</option>
                  <option value="comunidad">Comunidad</option>
                  <option value="familiar">Familiar</option>
                  <option value="empresa">Empresa</option>
                </Select>
                <Select
                  value={itemsPerPage.toString()}
                  onChange={(e) => {
                    setItemsPerPage(parseInt(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="w-32"
                >
                  <option value="10">10 por página</option>
                  <option value="25">25 por página</option>
                  <option value="50">50 por página</option>
                  <option value="100">100 por página</option>
                </Select>
                <Button color="gray" onClick={loadPrices} size="sm" className="gap-2 text-gray-900 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600">
                  <HiRefresh className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="xl" className="text-blue-600" />
          </div>
        ) : filteredPrices.length === 0 ? (
          <Card className="bg-white dark:bg-gray-800">
            <div className="py-12 text-center">
              <p className="text-gray-600 dark:text-gray-400 text-lg">No se encontraron precios</p>
            </div>
          </Card>
        ) : (
          <>
            {/* Vista de Tabla - Desktop */}
            <Card className="bg-white dark:bg-gray-800 hidden lg:block">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm text-gray-500 dark:text-gray-400">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-700 dark:bg-gray-700 dark:text-gray-400">
                    <tr>
                      <th scope="col" className="px-6 py-3">Clave</th>
                      <th scope="col" className="px-6 py-3">Etiqueta</th>
                      <th scope="col" className="px-6 py-3">Valor</th>
                      <th scope="col" className="px-6 py-3">Cantidad Mínima</th>
                      <th scope="col" className="px-6 py-3">Grupo</th>
                      <th scope="col" className="px-6 py-3">Orden</th>
                      <th scope="col" className="px-6 py-3">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedPrices.map((price) => (
                      <tr key={price.id} className="border-b bg-white dark:border-gray-700 dark:bg-gray-800">
                        <td className="whitespace-nowrap px-6 py-4 font-mono text-xs text-gray-900 dark:text-white">{price.key}</td>
                        <td className="px-6 py-4 font-medium text-gray-900 dark:text-white">{price.label}</td>
                        <td className="px-6 py-4">${parseFloat(price.value).toLocaleString('es-ES', { minimumFractionDigits: 2 })}</td>
                        <td className="px-6 py-4">{price.min_quantity}</td>
                        <td className="px-6 py-4">{getGroupBadge(price.group)}</td>
                        <td className="px-6 py-4">{price.order}</td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <Button
                              onClick={() => openEditModal(price)}
                              size="sm"
                              color="gray"
                              className="gap-1 text-gray-900 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                            >
                              <HiPencil className="w-3 h-3" />
                              Editar
                            </Button>
                            <Button
                              onClick={() => deletePrice(price)}
                              size="sm"
                              color="failure"
                              className="gap-1"
                            >
                              <HiTrash className="w-3 h-3" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* Vista de Tarjetas - Móvil */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:hidden">
              {displayedPrices.map((price) => (
                <Card key={price.id} className="hover:shadow-lg transition-shadow bg-white dark:bg-gray-800">
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">{price.label}</h3>
                        <p className="text-xs font-mono text-gray-500 dark:text-gray-400 mt-1">{price.key}</p>
                      </div>
                      <div className="flex flex-col gap-1 ml-2">
                        {getGroupBadge(price.group)}
                      </div>
                    </div>
                    <div className="space-y-1 text-sm mb-3">
                      <div className="text-gray-600 dark:text-gray-400">
                        <strong className="text-gray-900 dark:text-white">Valor:</strong> ${parseFloat(price.value).toLocaleString('es-ES', { minimumFractionDigits: 2 })}
                      </div>
                      <div className="text-gray-600 dark:text-gray-400">
                        <strong className="text-gray-900 dark:text-white">Cantidad Mínima:</strong> {price.min_quantity}
                      </div>
                      <div className="text-gray-600 dark:text-gray-400">
                        <strong className="text-gray-900 dark:text-white">Orden:</strong> {price.order}
                      </div>
                      {price.description && (
                        <div className="text-gray-500 dark:text-gray-400 text-xs">
                          {price.description}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <Button
                        onClick={() => openEditModal(price)}
                        size="sm"
                        color="gray"
                        className="flex-1 gap-1 text-gray-900 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                      >
                        <HiPencil className="w-3 h-3" />
                        Editar
                      </Button>
                      <Button
                        onClick={() => deletePrice(price)}
                        size="sm"
                        color="failure"
                        className="gap-1"
                      >
                        <HiTrash className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Paginación */}
            {filteredPrices.length > itemsPerPage && (
              <Card className="bg-white dark:bg-gray-800 p-0">
                <Pagination
                  currentPage={currentPage}
                  totalPages={Math.ceil(filteredPrices.length / itemsPerPage)}
                  onPageChange={(page) => setCurrentPage(page)}
                  totalItems={filteredPrices.length}
                  itemsPerPage={itemsPerPage}
                  showInfo={true}
                />
              </Card>
            )}
          </>
        )}

        {/* Modal de Edición */}
        <Modal show={showEditModal} onClose={closeEditModal} size="md">
          <div className="p-6 bg-white dark:bg-gray-800">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              {editingPrice ? 'Editar Precio' : 'Nuevo Precio'}
            </h3>
            <div className="space-y-4">
              {!editingPrice && (
                <div>
                  <Label htmlFor="key">Clave (única)</Label>
                  <TextInput
                    id="key"
                    type="text"
                    value={formData.key}
                    onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                    className="mt-1"
                    required
                  />
                </div>
              )}
              <div>
                <Label htmlFor="label">Etiqueta</Label>
                <TextInput
                  id="label"
                  type="text"
                  value={formData.label}
                  onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                  className="mt-1"
                  required
                />
              </div>
              <div>
                <Label htmlFor="value">Valor</Label>
                <TextInput
                  id="value"
                  type="number"
                  step="0.01"
                  value={formData.value}
                  onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                  className="mt-1"
                  required
                />
              </div>
              <div>
                <Label htmlFor="min_quantity">Cantidad Mínima</Label>
                <TextInput
                  id="min_quantity"
                  type="number"
                  value={formData.min_quantity}
                  onChange={(e) => setFormData({ ...formData, min_quantity: parseInt(e.target.value) || 0 })}
                  className="mt-1"
                  required
                />
              </div>
              <div>
                <Label htmlFor="group">Grupo</Label>
                <select
                  id="group"
                  value={formData.group}
                  onChange={(e) => setFormData({ ...formData, group: e.target.value as 'comunidad' | 'familiar' | 'empresa' })}
                  className="mt-1 block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-blue-500 dark:focus:ring-blue-500"
                >
                  <option value="comunidad">Comunidad</option>
                  <option value="familiar">Familiar</option>
                  <option value="empresa">Empresa</option>
                </select>
              </div>
              <div>
                <Label htmlFor="order">Orden</Label>
                <TextInput
                  id="order"
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                  className="mt-1"
                  required
                />
              </div>
              <div>
                <Label htmlFor="description">Descripción</Label>
                <TextInput
                  id="description"
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button color="blue" onClick={savePrice}>
                {editingPrice ? 'Guardar Cambios' : 'Crear Precio'}
              </Button>
              <Button color="gray" onClick={closeEditModal} className="text-gray-900 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600">
                Cancelar
              </Button>
            </div>
          </div>
        </Modal>

        {/* Modal de Confirmación */}
        <Modal show={showConfirmModal} onClose={() => {
          setShowConfirmModal(false);
          setConfirmAction(null);
          setConfirmMessage('');
        }} size="md">
          <div className="p-6 bg-white dark:bg-gray-800 text-center">
            <HiExclamationCircle className="mx-auto mb-4 h-12 w-12 text-red-600 dark:text-red-400" />
            <h3 className="mb-5 text-lg font-normal text-gray-900 dark:text-gray-200">
              {confirmMessage || (confirmAction ? '¿Estás seguro de realizar esta acción?' : '')}
            </h3>
            <div className="flex justify-center gap-4">
              <Button
                color="failure"
                onClick={() => {
                  if (confirmAction) confirmAction();
                  setShowConfirmModal(false);
                  setConfirmAction(null);
                  setConfirmMessage('');
                }}
                className="bg-red-600 hover:bg-red-700 text-white dark:bg-red-600 dark:hover:bg-red-700 dark:text-white font-semibold px-6"
              >
                Sí, estoy seguro
              </Button>
              <Button
                color="gray"
                onClick={() => {
                  setShowConfirmModal(false);
                  setConfirmAction(null);
                  setConfirmMessage('');
                }}
                className="text-gray-900 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                No, cancelar
              </Button>
            </div>
          </div>
        </Modal>

        {/* Modal de Mensaje */}
        <Modal show={messageModal.show} onClose={() => setMessageModal({ ...messageModal, show: false })} size="md">
          <div className="p-6 bg-white dark:bg-gray-800 text-center">
            {messageModal.type === 'success' && <HiCheckCircle className="mx-auto mb-4 h-12 w-12 text-green-500" />}
            {messageModal.type === 'error' && <HiXCircle className="mx-auto mb-4 h-12 w-12 text-red-500" />}
            {messageModal.type === 'info' && <HiInformationCircle className="mx-auto mb-4 h-12 w-12 text-blue-500" />}
            <h3 className="mb-2 text-xl font-bold text-gray-900 dark:text-white">{messageModal.title}</h3>
            <p className="mb-5 text-base text-gray-500 dark:text-gray-400">{messageModal.message}</p>
            <Button color="blue" onClick={() => setMessageModal({ ...messageModal, show: false })}>
              Cerrar
            </Button>
          </div>
        </Modal>
      </div>
    </AdminLayout>
  );
}
